import { create } from "zustand";
import { nanoid } from "nanoid";
import { PhoneValidator } from "../utils/phoneValidator";
import { capitalizeFirstLetter } from "../utils/capitalizeLetter";

interface ConsentData {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  address: string;
  address2: string;
  city: string;
  postalCode: string;
  province: string;
  dateOfBirth: Date | null;
  phone: string;
  consent: boolean;
  errors: { [key: string]: string };
}

interface ConsentApplicantsStore {
  consentApplicants: ConsentData[];
  addConsentApplicants: (id?: string) => void;
  validateConsent: () => boolean;
  updateConsentApplicantsField: (
    id: string,
    field: keyof ConsentData,
    value: any
  ) => void;
  validateConsentApplicants: () => boolean;
}

const useConsentApplicantsStore = create<
  ConsentApplicantsStore & { clearConsentApplicantsStore: () => void }
>()((set, get) => ({
  consentApplicants: [],

  addConsentApplicants: (id?: string) =>
    set((state) => ({
      consentApplicants: [
        ...state.consentApplicants,
        {
          id: id || nanoid(),
          firstName: "",
          middleName: "",
          lastName: "",
          address: "",
          address2: "",
          city: "",
          postalCode: "",
          province: "",
          email: "",
          phone: "",
          dateOfBirth: null,
          consent: false,
          errors: {},
        },
      ],
    })),

  clearConsentApplicantsStore: () =>
    set(() => ({
      consentApplicants: [],
    })),

  updateConsentApplicantsField: (id, field, value) =>
    set((state) => ({
      consentApplicants: state.consentApplicants.map((applicant) =>
        applicant.id === id
          ? {
              ...applicant,
              [field]:
                field === "firstName" ||
                field === "middleName" ||
                field === "lastName"
                  ? capitalizeFirstLetter(value)
                  : field === "postalCode"
                  ? value.toUpperCase()
                  : value,
              errors: {
                ...applicant.errors,
                [field]:
                  value === null || value === ""
                    ? "This field is required"
                    : "",
              },
            }
          : applicant
      ),
    })),

  validateConsent: () => {
    const { consentApplicants } = get();
    let allConsentsValid = true;

    const updatedConsentApplicants = consentApplicants.map((applicant) => {
      if (!applicant.consent) {
        allConsentsValid = false;
        return {
          ...applicant,
          errors: {
            ...applicant.errors,
            consent: "Consent is required",
          },
        };
      }
      return {
        ...applicant,
        errors: {
          ...applicant.errors,
          consent: "",
        },
      };
    });

    set({ consentApplicants: updatedConsentApplicants });

    return allConsentsValid;
  },

  validateConsentApplicants: () => {
    let isValid = true;

    set((state) => ({
      consentApplicants: state.consentApplicants.map((applicant) => {
        const errors = { ...applicant.errors };

        const requiredFields = [
          "firstName",
          "lastName",
          "address",
          "city",
          "postalCode",
          "province",
          "phone",
          "dateOfBirth",
        ];

        for (const field of requiredFields) {
          if (
            applicant[field as keyof ConsentData] === null ||
            applicant[field as keyof ConsentData] === ""
          ) {
            errors[field] = "This field is required";
            isValid = false;
          }
        }

        if (applicant.phone) {
          try {
            const isValidPhone =
              PhoneValidator.isValid(applicant.phone, "CA") ||
              PhoneValidator.isValid(applicant.phone, "US");

            if (!isValidPhone) {
              errors.phone = "Invalid phone number format for CA or US";
              isValid = false;
            }
          } catch (error) {
            errors.phone = "Invalid phone number format";
            isValid = false;
          }
        }

        return { ...applicant, errors };
      }),
    }));

    return isValid;
  },
}));

export { useConsentApplicantsStore };
